import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { onMounted, onUnmounted } from 'vue'

// Registrar o plugin ScrollTrigger
gsap.registerPlugin(ScrollTrigger)

export function useScrollAnimations() {
  let scrollTriggers = []

  const fadeInUp = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element, index) => {
      gsap.set(element, { opacity: 0, y: 60 })
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        end: 'bottom 20%',
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            y: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power2.out',
            ...options
          })
        },
        onLeave: () => {
          if (options.reverse) {
            gsap.to(element, {
              opacity: 0,
              y: -60,
              duration: 0.6,
              ease: 'power2.in'
            })
          }
        },
        onEnterBack: () => {
          if (options.reverse) {
            gsap.to(element, {
              opacity: 1,
              y: 0,
              duration: 0.8,
              ease: 'power2.out'
            })
          }
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const fadeInLeft = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element, index) => {
      gsap.set(element, { opacity: 0, x: -80 })
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power2.out',
            ...options
          })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const fadeInRight = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element, index) => {
      gsap.set(element, { opacity: 0, x: 80 })
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            x: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power2.out',
            ...options
          })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const scaleInView = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element, index) => {
      gsap.set(element, { opacity: 0, scale: 0.8 })
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            scale: 1,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'back.out(1.7)',
            ...options
          })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const parallaxEffect = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element) => {
      const speed = options.speed || 0.5
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top bottom',
        end: 'bottom top',
        scrub: true,
        onUpdate: (self) => {
          const y = self.progress * 100 * speed
          gsap.set(element, { y: -y })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const staggerText = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element) => {
      const words = element.textContent.split(' ')
      element.innerHTML = words.map(word => 
        `<span class="word" style="display: inline-block; opacity: 0; transform: translateY(20px);">${word}</span>`
      ).join(' ')
      
      const wordElements = element.querySelectorAll('.word')
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(wordElements, {
            opacity: 1,
            y: 0,
            duration: 0.6,
            stagger: 0.1,
            ease: 'power2.out',
            ...options
          })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const rotateInView = (selector, options = {}) => {
    const elements = document.querySelectorAll(selector)
    
    elements.forEach((element, index) => {
      gsap.set(element, { opacity: 0, rotation: -15 })
      
      const trigger = ScrollTrigger.create({
        trigger: element,
        start: 'top 80%',
        onEnter: () => {
          gsap.to(element, {
            opacity: 1,
            rotation: 0,
            duration: 0.8,
            delay: index * 0.1,
            ease: 'power2.out',
            ...options
          })
        }
      })
      
      scrollTriggers.push(trigger)
    })
  }

  const cleanup = () => {
    scrollTriggers.forEach(trigger => trigger.kill())
    scrollTriggers = []
  }

  const refresh = () => {
    ScrollTrigger.refresh()
  }

  onUnmounted(() => {
    cleanup()
  })

  return {
    fadeInUp,
    fadeInLeft,
    fadeInRight,
    scaleInView,
    parallaxEffect,
    staggerText,
    rotateInView,
    cleanup,
    refresh
  }
} 